"use client";

import { cn } from "@/lib/utils";
import React, { useEffect, useRef, useState } from "react";

interface RichTextWrapperProps {
  content: string;
  className?: string;
  pClassName?: string;
  hClassName?: string;
}

const BASE_STYLES = `
  * { box-sizing: border-box; }
  h1,h2,h3,h4,h5,h6 {
    font-weight: bold;
    margin-top: 1em;
    margin-bottom: 0.5em;
    line-height: 1.3;
  }
  h1 { font-size: 2em; }
  h2 { font-size: 1.5em; }
  h3 { font-size: 1.25em; }
  h4 { font-size: 1.1em; }
  h5 { font-size: 1em; }
  h6 { font-size: 0.9em; }
  p { margin-bottom: 1em; line-height: 1.6; }
  a { color: #0066cc; text-decoration: underline; }
  a:hover { opacity: 0.8; }
  ul,ol { margin-left: 1.5em; margin-bottom: 1em; line-height: 1.6; }
  li { margin-bottom: 0.5em; }
  ul { list-style-type: disc; }
  ol { list-style-type: decimal; }
  img { max-width: 100%; height: auto; display: block; }
  table { border-collapse: collapse; width: 100%; margin-bottom: 1em; }
  th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
  th { background-color: #f2f2f2; font-weight: bold; }
  strong, b { font-weight: bold; }
  em, i { font-style: italic; }
`;

const hasThirdPartyScripts = (html: string): boolean => {
  const scriptTagRegex = /<script[^>]+src=["']([^"']+)["'][^>]*>/gi;
  let match;

  while ((match = scriptTagRegex.exec(html)) !== null) {
    const src = match[1];

    if (!src || src.startsWith("/") || src.startsWith("./") || src.startsWith("../")) {
      continue;
    }

    try {
      const url = new URL(src);
      if (url.hostname !== window.location.hostname) {
        return true;
      }
    } catch {
      continue;
    }
  }

  return false;
};

// ─────────────────────────────────────────────
// Shadow DOM renderer — for first-party content
// ─────────────────────────────────────────────
const ShadowRenderer: React.FC<{
  content: string;
  className?: string;
  pClassName?: string;
  hClassName?: string;
}> = ({ content, className, pClassName, hClassName }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    containerRef.current.innerHTML = "";

    let shadowRoot: ShadowRoot;
    if (containerRef.current.shadowRoot) {
      shadowRoot = containerRef.current.shadowRoot;
    } else {
      shadowRoot = containerRef.current.attachShadow({ mode: "open" });
    }

    const styleSheet = `
      <style>
        :host {
          display: block;
          width: 100%;
          padding: 0 1.25rem;
          box-sizing: border-box;
        }
        ${BASE_STYLES}
        ${pClassName ? `p { ${pClassName} }` : ""}
        ${hClassName ? `h1,h2,h3,h4,h5,h6 { ${hClassName} }` : ""}
      </style>
    `;

    shadowRoot.innerHTML = styleSheet + content;

    // Handle link clicks: open in new tab only for external links or target="_blank"
    shadowRoot.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a") as HTMLAnchorElement | null;
      if (!anchor || !anchor.href) return;

      try {
        const url = new URL(anchor.href);
        const isExternal = url.hostname !== window.location.hostname;
        const isBlankTarget = anchor.target === "_blank";
        if (
          (isExternal || isBlankTarget) &&
          (url.protocol === "https:" || url.protocol === "http:")
        ) {
          e.preventDefault();
          window.open(anchor.href, "_blank", "noopener,noreferrer");
        }
      } catch {
        // Ignore malformed URLs
      }
    });

    const executeScriptsInOrder = (
      scripts: NodeListOf<HTMLScriptElement>
    ) => {
      const arr = Array.from(scripts);

      const next = (i: number) => {
        if (i >= arr.length) return;

        const oldScript = arr[i];
        const newScript = document.createElement("script");

        Array.from(oldScript.attributes).forEach((attr) =>
          newScript.setAttribute(attr.name, attr.value)
        );

        if (oldScript.src) {
          newScript.onload = () => next(i + 1);
          newScript.onerror = () => next(i + 1);
          oldScript.replaceWith(newScript);
        } else {
          (window as any).__currentShadowRoot__ = shadowRoot;

          newScript.textContent = `
            (function(__shadowRoot__) {
              const document = new Proxy(window.document, {
                get(target, prop) {
                  if (prop === 'querySelector')
                    return (sel) => __shadowRoot__.querySelector(sel);
                  if (prop === 'querySelectorAll')
                    return (sel) => __shadowRoot__.querySelectorAll(sel);
                  if (prop === 'getElementById')
                    return (id) => __shadowRoot__.querySelector('#' + id);
                  if (prop === 'currentScript')
                    return { getRootNode: () => __shadowRoot__ };
                  const val = target[prop];
                  return typeof val === 'function' ? val.bind(target) : val;
                }
              });
              ${oldScript.textContent}
            })(window.__currentShadowRoot__);
          `;

          oldScript.replaceWith(newScript);
          next(i + 1);
        }
      };

      next(0);
    };

    executeScriptsInOrder(shadowRoot.querySelectorAll("script"));
  }, [content, pClassName, hClassName]);

  return (
    <div
      ref={containerRef}
      className={cn(
        "rich-text-shadow-wrapper w-full max-w-screen overflow-x-hidden",
        className
      )}
    />
  );
};

// ─────────────────────────────────────────────
// iframe renderer — for third-party scripts
// ─────────────────────────────────────────────
const IframeRenderer: React.FC<{
  content: string;
  className?: string;
  pClassName?: string;
  hClassName?: string;
}> = ({ content, className, pClassName, hClassName }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIframeHeight] = useState<number>(0);

  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      if (e.data?.type === "iframe-height") {
        setIframeHeight(e.data.height + 80);
      }

      if (e.data?.type === "open-link" && e.data?.href) {
        try {
          const url = new URL(e.data.href);
          if (url.protocol === "https:" || url.protocol === "http:") {

            const newTab = window.open("about:blank", "_blank");
            if (newTab) {
              newTab.document.write(
                `<meta name="referrer" content="no-referrer">` +
                `<script>window.location.replace("${url.href.replace(/"/g, "%22")}")<\/script>`
              );
              newTab.document.close();
            }
          }
        } catch (_) {
          // Ignore malformed URLs
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  useEffect(() => {
    if (!iframeRef.current) return;

    const iframe = iframeRef.current;
    const iframeDoc = iframe.contentDocument;
    if (!iframeDoc) return;

    iframeDoc.open();
    iframeDoc.write(`
      <!DOCTYPE html>
      <html lang="en">
        <head>
          <meta charset="UTF-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <style>
            ${BASE_STYLES}
            body {
              margin: 0;
              padding: 0 1.25rem;
              box-sizing: border-box;
              overflow-x: hidden;
            }
            ${pClassName ? `p { ${pClassName} }` : ""}
            ${hClassName ? `h1,h2,h3,h4,h5,h6 { ${hClassName} }` : ""}
          </style>
        </head>
        <body>
          ${content}
          <script>
            document.addEventListener('click', function(e) {
              const anchor = e.target.closest('a');
              if (anchor && anchor.href) {
                try {
                  const url = new URL(anchor.href);
                  const isExternal = url.hostname !== window.location.hostname;
                  const isBlankTarget = anchor.target === '_blank';
                  if (
                    (isExternal || isBlankTarget) &&
                    (url.protocol === 'https:' || url.protocol === 'http:')
                  ) {
                    e.preventDefault();
                    e.stopPropagation();
                    window.parent.postMessage(
                      { type: 'open-link', href: anchor.href },
                      '*'
                    );
                  }
                } catch (_) {}
              }
            }, true);

            const notifyHeight = () => {
              window.parent.postMessage(
                { type: 'iframe-height', height: document.body.scrollHeight },
                '*'
              );
            };

            notifyHeight();

            new MutationObserver(notifyHeight).observe(document.body, {
              childList: true,
              subtree: true,
              attributes: true,
            });

            window.addEventListener('load', notifyHeight);
          <\/script>
        </body>
      </html>
    `);
    iframeDoc.close();
  }, [content, pClassName, hClassName]);

  return (
    <iframe
      ref={iframeRef}
      className={cn("w-full border-none overflow-hidden block", className)}
      style={{ height: iframeHeight > 0 ? `${iframeHeight}px` : "auto" }}
      sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-popups-to-escape-sandbox allow-top-navigation"
      title="rich-text-content"
    />
  );
};

// ─────────────────────────────────────────────
// Main component — auto-selects renderer
// ─────────────────────────────────────────────
const RichTextWrapper: React.FC<RichTextWrapperProps> = ({
  content,
  className,
  pClassName,
  hClassName,
}) => {
  const isThirdParty = hasThirdPartyScripts(content);

  if (isThirdParty) {
    return (
      <IframeRenderer
        content={content}
        className={className}
        pClassName={pClassName}
        hClassName={hClassName}
      />
    );
  }

  return (
    <ShadowRenderer
      content={content}
      className={className}
      pClassName={pClassName}
      hClassName={hClassName}
    />
  );
};

export default RichTextWrapper;